import { Button, Card, Cascader, Form, Input, Radio, Select, Space, Typography } from "antd";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { BaseItem as ZhBaseItem, Dialog as ZhDialog, Grid as ZhGrid, ZhMap } from "zhihao-ui-react";
import type { TiandituVo } from "@zhihaoscm/entity";

import request from "@/api";
import { sliceGbCode } from "@/views/pages/logistics/wharf";
import { validatePhone } from "@/utils/validate";
import { loadGroupCompanies as loadGroupCompanyOptions } from "./SupplierPage";
import type { GroupCompanyOption, SupplierFormValues, SupplierRow } from "./types";

export interface SupplierFormPageProps {
    loadGroupCompanies?: (name: string) => Promise<GroupCompanyOption[]>;
    loadAreaOptions?: () => Promise<SupplierAreaOption[]>;
    loadAddressSuggestions?: (keyword: string, regionCode?: string) => Promise<SupplierAddressSuggestion[]>;
    loadSupplier?: (id: number | string) => Promise<SupplierRow>;
    saveSupplier?: (id: number | string | undefined, values: SupplierFormValues) => Promise<void>;
}

interface SupplierAreaOption {
    value: string;
    label: string;
    children?: SupplierAreaOption[];
}

interface SupplierAddressSuggestion {
    value: string;
    address?: string;
    lonlat?: string;
    countyCode?: string;
}

/** 复用 Vue 供应商详情接口，为编辑表单回填数据。 */
export async function loadSupplier(id: number | string): Promise<SupplierRow> {
    const response = await request.service.get<SupplierRow>(`/admin/supplier-admin/find/${id}`);
    return response.data;
}

/** 复用 Vue areaLib 的静态行政区数据，延迟加载以避免表单首屏阻塞。 */
export async function loadSupplierAreaOptions(): Promise<SupplierAreaOption[]> {
    const response = await fetch("https://static.zhihaoscm.cn/map/areas.json");
    if (!response.ok) throw new Error("区域数据加载失败");
    const data = (await response.json()) as Array<{
        id: string;
        name: string;
        childs?: Array<{ id: string; name: string; childs?: unknown[] }>;
    }>;
    const toOptions = (items: typeof data): SupplierAreaOption[] =>
        items.map(item => ({
            value: item.id,
            label: item.name,
            children: item.childs ? toOptions(item.childs as typeof data) : undefined
        }));
    return toOptions(data);
}

/** 复用 Vue 供应商地址联想的天地图查询参数和经纬度结果。 */
export async function loadSupplierAddressSuggestions(
    keyword: string,
    regionCode = ""
): Promise<SupplierAddressSuggestion[]> {
    if (!keyword.trim()) return [];
    const { data: result } = await request.service.post<TiandituVo>("/admin/tianditu/search?type=query", {
        keyWord: keyword.trim(),
        mapBound: "-180,-90,180,90",
        level: 18,
        queryType: regionCode.trim() ? "7" : "4",
        show: "2",
        start: "0",
        count: "10",
        ...(regionCode.trim() ? { specify: regionCode.trim() } : {})
    });
    const pois = (result.pois || []).map(item => ({
        value: item.address,
        address: item.address,
        lonlat: item.lonlat,
        countyCode: item.countyCode
    }));
    const suggests = (result.suggests || []).map(item => ({
        value: item.name,
        address: item.address,
        lonlat: item.lonlat,
        countyCode: item.gbCode
    }));
    return [...suggests, ...pois];
}

/** 复用 Vue 供应商新增/修改接口。 */
export async function saveSupplier(id: number | string | undefined, values: SupplierFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/supplier-admin/create", values);
    } else {
        await request.service.put(`/admin/supplier-admin/update/${id}`, values);
    }
}

/** 将 Vue 供应商编辑返回的类型和展示位置枚举统一为表单使用的数字。 */
function normalizeSupplierFormValues(supplier: SupplierFormValues): SupplierFormValues {
    return {
        ...supplier,
        type: supplier.type == null ? 1 : Number(supplier.type),
        showLocation: supplier.showLocation == null ? 1 : Number(supplier.showLocation)
    };
}

/** 将 Vue 地图弹窗使用的经纬度文本转换为 zhihao 地图中心点。 */
function parseMapCenter(latLon?: string): [number, number] | undefined {
    const coordinates = latLon?.split(",").map(value => Number(value.trim()));
    if (!coordinates || coordinates.length < 2 || coordinates.some(value => !Number.isFinite(value))) return undefined;
    return [coordinates[0], coordinates[1]];
}

/** 供应商新增/编辑页，覆盖核心供应商字段并保留 Vue 保存契约。 */
export function SupplierFormPage({
    loadGroupCompanies: loadGroups = loadGroupCompanyOptions,
    loadAreaOptions: loadAreas = loadSupplierAreaOptions,
    loadAddressSuggestions: loadAddresses = loadSupplierAddressSuggestions,
    loadSupplier: load = loadSupplier,
    saveSupplier: save = saveSupplier
}: SupplierFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const id = new URLSearchParams(location.search).get("id") || undefined;
    const [form] = Form.useForm<SupplierFormValues>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isRelateGroup, setIsRelateGroup] = useState(false);
    const [groupSearch, setGroupSearch] = useState("");
    const [selectedGroupName, setSelectedGroupName] = useState<string | undefined>();
    const [groupOptions, setGroupOptions] = useState<GroupCompanyOption[]>([]);
    const [areaOptions, setAreaOptions] = useState<SupplierAreaOption[]>([]);
    const [areaLoading, setAreaLoading] = useState(false);
    const [areaLoaded, setAreaLoaded] = useState(false);
    const [enterpriseTags, setEnterpriseTags] = useState<Array<{ value: string }>>([{ value: "" }]);
    const [mapOpen, setMapOpen] = useState(false);
    const [mapAddress, setMapAddress] = useState("");
    const [mapLatLon, setMapLatLon] = useState("");
    const [addressSuggestions, setAddressSuggestions] = useState<SupplierAddressSuggestion[]>([]);
    const [addressLoading, setAddressLoading] = useState(false);

    /** 沿用 Vue ImgList 的单文件上传契约，将文件服务返回的 ID 回填到供应商表单。 */
    const uploadSupplierFile = async (
        field: "logoFileId" | "businessFileId" | "mobileSideFileId" | "pcSideFileId",
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        // 后台文件服务统一限制 10MB，避免超大附件进入保存流程。
        if (file.size > 10 * 1024 * 1024) {
            setErrorMessage("供应商附件不能超过10MB");
            return;
        }
        try {
            const formData = new FormData();
            formData.append("file", file);
            const { fileService } = await import("@/api/services/fileService");
            const uploaded = await fileService.uploadFile(formData);
            form.setFieldsValue({ [field]: uploaded.id } as Partial<SupplierFormValues>);
            setErrorMessage(null);
        } catch {
            setErrorMessage("供应商附件上传失败");
        }
    };

    useEffect(() => {
        if (!id) {
            form.setFieldsValue({ supplierName: "", type: 1, showLocation: 1, legalRepresentative: "" });
            return;
        }
        void load(id)
            .then(result => {
                const supplier = normalizeSupplierFormValues(result.supplier as SupplierFormValues);
                form.setFieldsValue({
                    ...supplier,
                    areaCode: [supplier.provinceCode, supplier.cityCode, supplier.regionCode].filter(
                        (value): value is string => Boolean(value)
                    ),
                    labelsText: supplier.labels?.join(","),
                    latLon: supplier.geo ? [supplier.geo.lng || "", supplier.geo.lat || ""].join(",") : undefined
                });
                setEnterpriseTags(
                    supplier.labels?.length ? supplier.labels.map(value => ({ value })) : [{ value: "" }]
                );
                setIsRelateGroup(Boolean(supplier.groupCompanyId));
                setSelectedGroupName(supplier.groupCompanyName);
                if (supplier.groupCompanyId && supplier.groupCompanyName) {
                    setGroupSearch(supplier.groupCompanyName);
                    void loadGroups(supplier.groupCompanyName)
                        .then(setGroupOptions)
                        .catch(() => {
                            // 编辑回显集团候选失败时保留表单，并提示远程数据暂不可用。
                            setGroupOptions([]);
                            setErrorMessage("集团查询失败");
                        });
                }
            })
            .catch(() => setErrorMessage("供应商详情加载失败"));
    }, [form, id, load, loadGroups]);

    /** 仅在用户展开区域控件时加载行政区，保持 Vue 页面可用且不拖慢首屏。 */
    const ensureAreaOptions = async () => {
        if (areaLoaded || areaLoading) return;
        setAreaLoading(true);
        try {
            setAreaOptions((await loadAreas()) || []);
            setAreaLoaded(true);
        } catch {
            setErrorMessage("区域数据加载失败");
        } finally {
            setAreaLoading(false);
        }
    };

    /** 按旧 Vue 地址联想行为查询 POI，选中后回填地址和坐标。 */
    const searchAddresses = async () => {
        const keyword = String(form.getFieldValue("address") || "").trim();
        if (!keyword) {
            setAddressSuggestions([]);
            return;
        }
        setAddressLoading(true);
        try {
            setAddressSuggestions((await loadAddresses(keyword, form.getFieldValue("regionCode"))) || []);
        } catch {
            setAddressSuggestions([]);
            setErrorMessage("地址联想查询失败");
        } finally {
            setAddressLoading(false);
        }
    };

    // 沿用 Vue remote select：空关键词不请求集团 selector。
    const searchGroups = async (name: string) => {
        setGroupSearch(name);
        if (!name.trim()) {
            setGroupOptions([]);
            return;
        }
        try {
            setGroupOptions((await loadGroups(name)) || []);
        } catch {
            // 集团候选查询失败时清空旧选项，避免误选，并反馈接口异常。
            setGroupOptions([]);
            setErrorMessage("集团查询失败");
        }
    };

    const submit = async () => {
        try {
            const values = Object.fromEntries(
                Object.entries(await form.validateFields()).filter(([, value]) => value !== undefined)
            ) as SupplierFormValues;
            // 级联控件只是编辑辅助字段，Vue 接口仍只接收 province/city/region 三个编码。
            delete values.areaCode;
            const submitValues: SupplierFormValues = isRelateGroup
                ? {
                      ...values,
                      groupCompanyId: Number(values.groupCompanyId),
                      groupCompanyName: selectedGroupName
                  }
                : (() => {
                      const unlinkedValues = { ...values };
                      delete unlinkedValues.groupCompanyId;
                      delete unlinkedValues.groupCompanyName;
                      return unlinkedValues;
                  })();
            if (submitValues.latLon) {
                const [lng, lat] = submitValues.latLon.split(",");
                submitValues.geo = { lng, lat };
                delete submitValues.latLon;
            }
            // Vue 接口接收标签数组，React 表单使用逗号文本便于快速录入后再统一转换。
            if (submitValues.labelsText !== undefined) {
                const labels = submitValues.labelsText
                    .split(",")
                    .map(label => label.trim())
                    .filter(Boolean);
                delete submitValues.labelsText;
                if (labels.length > 0) {
                    submitValues.labels = labels;
                } else {
                    delete submitValues.labels;
                }
            } else {
                const labels = enterpriseTags.map(tag => tag.value.trim()).filter(Boolean);
                if (labels.length > 0) submitValues.labels = labels;
            }
            await save(id, submitValues);
            void navigate("/supplierManagement/enterpriseManage");
        } catch {
            setErrorMessage("供应商保存失败");
        }
    };

    return (
        <section aria-label="supplier-form-page">
            <Card>
                <Typography.Title level={4}>{id ? "修改供应商" : "新增供应商"}</Typography.Title>
                {errorMessage ? <p role="alert">{errorMessage}</p> : null}
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="供应商名称"
                        name="supplierName"
                        rules={[
                            { required: true, message: "请输入供应商名称" },
                            { max: 32, message: "不超过32位字符" }
                        ]}
                    >
                        <Input allowClear />
                    </Form.Item>
                    <Form.Item label="是否关联集团">
                        <Radio.Group
                            value={isRelateGroup ? 1 : 0}
                            options={[
                                { label: "是", value: 1 },
                                { label: "否", value: 0 }
                            ]}
                            onChange={event => {
                                const related = event.target.value === 1;
                                setIsRelateGroup(related);
                                if (!related) {
                                    form.setFieldsValue({ groupCompanyId: undefined, groupCompanyName: undefined });
                                    setGroupSearch("");
                                    setSelectedGroupName(undefined);
                                    setGroupOptions([]);
                                }
                            }}
                        />
                    </Form.Item>
                    {isRelateGroup ? (
                        <Form.Item label="集团" required>
                            <Space>
                                <Input
                                    placeholder="请输入关键词以搜索集团"
                                    value={groupSearch}
                                    onChange={event => void searchGroups(event.target.value)}
                                />
                                <Form.Item
                                    noStyle
                                    name="groupCompanyId"
                                    rules={[{ required: true, message: "请选择集团" }]}
                                >
                                    <select
                                        aria-label="集团"
                                        onChange={event => {
                                            const selected = groupOptions.find(
                                                option => String(option.id) === event.target.value
                                            );
                                            form.setFieldsValue({
                                                groupCompanyId: selected ? Number(selected.id) : undefined
                                            });
                                            setSelectedGroupName(selected?.name);
                                        }}
                                    >
                                        <option value="">请选择集团</option>
                                        {groupOptions.map(option => (
                                            <option key={option.id} value={String(option.id)}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </Form.Item>
                            </Space>
                        </Form.Item>
                    ) : null}
                    <Form.Item label="所在区域（级联）" name="areaCode">
                        <Cascader
                            aria-label="所在区域（级联）"
                            loading={areaLoading}
                            options={areaOptions}
                            placeholder="请选择省市区"
                            onFocus={() => void ensureAreaOptions()}
                            onChange={value => {
                                const codes = value.map(String);
                                form.setFieldsValue({
                                    areaCode: codes,
                                    provinceCode: codes[0],
                                    cityCode: codes[1],
                                    regionCode: codes[2]
                                });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label="省份编码" name="provinceCode" rules={[{ required: true, message: "请选择省份" }]}>
                        <Input required placeholder="请输入省份编码" />
                    </Form.Item>
                    <Form.Item label="城市编码" name="cityCode" rules={[{ required: true, message: "请选择地级市" }]}>
                        <Input required placeholder="请输入城市编码" />
                    </Form.Item>
                    <Form.Item label="区域编码" name="regionCode" rules={[{ required: true, message: "请选择县区" }]}>
                        <Input required placeholder="请输入区域编码" />
                    </Form.Item>
                    <Form.Item label="详细地址">
                        <Space.Compact className="w-full">
                            <Form.Item noStyle name="address" rules={[{ required: true, message: "请输入详细地址" }]}>
                                <Input
                                    allowClear
                                    aria-label="详细地址"
                                    required
                                    maxLength={200}
                                    placeholder="请输入详细地址"
                                />
                            </Form.Item>
                            <Button loading={addressLoading} onClick={() => void searchAddresses()}>
                                地址联想
                            </Button>
                        </Space.Compact>
                    </Form.Item>
                    {addressSuggestions.length ? (
                        <div aria-label="地址联想结果" className="mb-4 flex flex-col gap-1">
                            {addressSuggestions.map((suggestion, index) => (
                                <Button
                                    key={`${suggestion.value}-${index}`}
                                    className="!h-auto !justify-start !whitespace-normal !text-left"
                                    type="link"
                                    onClick={() => {
                                        // 沿用 Vue：未选区域时从天地图 countyCode 补齐省市区，已有区域不被联想结果覆盖。
                                        const areaCode =
                                            !form.getFieldValue("regionCode") && suggestion.countyCode
                                                ? sliceGbCode(suggestion.countyCode)
                                                : [];
                                        form.setFieldsValue({
                                            address: suggestion.address || suggestion.value,
                                            latLon: suggestion.lonlat,
                                            ...(areaCode.length
                                                ? {
                                                      areaCode,
                                                      provinceCode: areaCode[0],
                                                      cityCode: areaCode[1],
                                                      regionCode: areaCode[2]
                                                  }
                                                : {})
                                        });
                                        setAddressSuggestions([]);
                                    }}
                                >
                                    {suggestion.value}
                                    {suggestion.address && suggestion.address !== suggestion.value
                                        ? ` ${suggestion.address}`
                                        : ""}
                                </Button>
                            ))}
                        </div>
                    ) : null}
                    <Form.Item label="经纬度">
                        <Space.Compact className="w-full">
                            <Form.Item noStyle name="latLon" rules={[{ required: true, message: "请选择经纬度" }]}>
                                <Input aria-label="经纬度" required placeholder="请输入经度,纬度" />
                            </Form.Item>
                            <Button
                                aria-label="地图选点"
                                onClick={() => {
                                    setMapAddress(form.getFieldValue("address") || "");
                                    setMapLatLon(form.getFieldValue("latLon") || "");
                                    setMapOpen(true);
                                }}
                            >
                                地图选点
                            </Button>
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item
                        label="主营品类"
                        name="mainCategories"
                        rules={[
                            { required: true, message: "请输入主营品类" },
                            { max: 50, message: "不超过50位字符" }
                        ]}
                    >
                        <Input allowClear required placeholder="请输入主营品类" maxLength={50} showCount />
                    </Form.Item>
                    <Form.Item label="企业 Logo 文件 ID">
                        <div>
                            <Form.Item noStyle name="logoFileId">
                                <Input aria-label="企业 Logo 文件 ID" placeholder="请输入企业 Logo 文件 ID" />
                            </Form.Item>
                            <input
                                type="file"
                                accept="image/jpeg,image/png"
                                aria-label="上传企业 Logo"
                                onChange={event => void uploadSupplierFile("logoFileId", event)}
                            />
                        </div>
                    </Form.Item>
                    <Form.Item label="营业执照文件 ID">
                        <div>
                            <Form.Item
                                noStyle
                                name="businessFileId"
                                rules={[{ required: true, message: "请上传营业执照" }]}
                            >
                                <Input aria-label="营业执照文件 ID" required placeholder="请输入营业执照文件 ID" />
                            </Form.Item>
                            <input
                                type="file"
                                accept="image/jpeg,image/png"
                                aria-label="上传营业执照"
                                onChange={event => void uploadSupplierFile("businessFileId", event)}
                            />
                        </div>
                    </Form.Item>
                    <Form.Item label="移动端首页广告文件 ID">
                        <div>
                            <Form.Item noStyle name="mobileSideFileId">
                                <Input aria-label="移动端首页广告文件 ID" placeholder="请输入移动端首页广告文件 ID" />
                            </Form.Item>
                            <input
                                type="file"
                                accept="image/jpeg,image/png"
                                aria-label="上传移动端首页广告"
                                onChange={event => void uploadSupplierFile("mobileSideFileId", event)}
                            />
                        </div>
                    </Form.Item>
                    <Form.Item label="PC端首页广告文件 ID">
                        <div>
                            <Form.Item noStyle name="pcSideFileId">
                                <Input aria-label="PC端首页广告文件 ID" placeholder="请输入 PC 端首页广告文件 ID" />
                            </Form.Item>
                            <input
                                type="file"
                                accept="image/jpeg,image/png"
                                aria-label="上传 PC 端首页广告"
                                onChange={event => void uploadSupplierFile("pcSideFileId", event)}
                            />
                        </div>
                    </Form.Item>
                    <Form.Item label="企业标签" name="labelsText">
                        <Input placeholder="多个标签用逗号分隔" />
                    </Form.Item>
                    <div aria-label="企业标签编辑器" className="mb-4 flex flex-wrap items-center gap-2">
                        {enterpriseTags.map((tag, index) => (
                            <Space key={`enterprise-tag-${index}`} size="small">
                                <Input
                                    aria-label={`企业标签-${index + 1}`}
                                    maxLength={6}
                                    placeholder="请输入企业标签"
                                    value={tag.value}
                                    onChange={event => {
                                        const value = event.target.value;
                                        setEnterpriseTags(current =>
                                            current.map((currentTag, currentIndex) =>
                                                currentIndex === index ? { value } : currentTag
                                            )
                                        );
                                    }}
                                />
                                <Button
                                    aria-label={`删除企业标签-${index + 1}`}
                                    disabled={enterpriseTags.length === 1}
                                    onClick={() =>
                                        setEnterpriseTags(current =>
                                            current.filter((_, currentIndex) => currentIndex !== index)
                                        )
                                    }
                                >
                                    删除
                                </Button>
                            </Space>
                        ))}
                        <Button
                            aria-label="添加企业标签"
                            disabled={enterpriseTags.length >= 5}
                            onClick={() => setEnterpriseTags(current => [...current, { value: "" }])}
                        >
                            添加企业标签
                        </Button>
                    </div>
                    <Form.Item label="类型" name="type" rules={[{ required: true }]}>
                        <Radio.Group
                            options={[
                                { label: "企业", value: 1 },
                                { label: "个人", value: 2 }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item label="展示位置" name="showLocation">
                        <Select
                            options={[
                                { label: "首页", value: 1 },
                                { label: "供应商列表", value: 2 }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item label="联系人" name="contactPerson">
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="联系电话"
                        name="contactPhone"
                        rules={[
                            {
                                validator: (_, value) =>
                                    !value || validatePhone(String(value))
                                        ? Promise.resolve()
                                        : Promise.reject(new Error("请输入正确的手机号")),
                                message: "请输入正确的手机号"
                            }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="统一社会信用代码"
                        name="socialCreditCode"
                        rules={[
                            { required: true, message: "请输入统一社会信用代码" },
                            { pattern: /^[A-Z0-9]{18}$/, message: "请输入有效的18位统一社会信用代码" }
                        ]}
                    >
                        <Input allowClear aria-label="统一社会信用代码" required />
                    </Form.Item>
                    <Form.Item
                        label="法人姓名"
                        name="legalRepresentative"
                        rules={[
                            { required: true, message: "请输入法人姓名" },
                            { max: 10, message: "不超过10位字符" }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item label="介绍" name="description">
                        <Input.TextArea allowClear maxLength={300} rows={3} showCount />
                    </Form.Item>
                    <Space>
                        <Button type="primary" onClick={() => void submit()}>
                            提交供应商
                        </Button>
                        <Button onClick={() => void navigate("/supplierManagement/enterpriseManage")}>返回</Button>
                    </Space>
                </Form>
            </Card>
            <ZhDialog
                open={mapOpen}
                title="供应商位置选择"
                width="860px"
                footer={
                    <Space>
                        <Button onClick={() => setMapOpen(false)}>取消</Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                form.setFieldsValue({ address: mapAddress, latLon: mapLatLon });
                                setMapOpen(false);
                            }}
                        >
                            确定位置
                        </Button>
                    </Space>
                }
                onCancel={() => setMapOpen(false)}
            >
                <ZhGrid cols={1} gap={[12, 12]}>
                    <ZhBaseItem label="当前坐标" value={mapLatLon || "未选择"} />
                    <Input
                        aria-label="地图地址"
                        placeholder="请输入地图地址"
                        value={mapAddress}
                        onChange={event => setMapAddress(event.target.value)}
                    />
                    <Input
                        aria-label="地图经纬度"
                        placeholder="请输入经度,纬度"
                        value={mapLatLon}
                        onChange={event => setMapLatLon(event.target.value)}
                    />
                </ZhGrid>
                <ZhMap center={parseMapCenter(mapLatLon)} className="mt-4 h-80 w-full" showScale zoom={13} />
            </ZhDialog>
        </section>
    );
}

export default SupplierFormPage;
